import { Component, OnDestroy, signal } from '@angular/core';
import { Auth, sendEmailVerification, signOut } from '@angular/fire/auth';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthOnboardingService, OnboardingProfile } from '../../../services/auth-onboarding.service';
import { SubscriptionService } from '../../../services/subscription.service';

@Component({
  selector: 'app-verify-email',
  templateUrl: './verify-email.component.html',
  styleUrl: './verify-email.component.css'
})
export class VerifyEmailComponent implements OnDestroy {
  readonly isChecking = signal(false);
  readonly isResending = signal(false);
  readonly message = signal<string | null>(null);
  readonly errorMessage = signal<string | null>(null);
  readonly resendCooldown = signal(0);
  readonly email = this.auth.currentUser?.email ?? '';
  private cooldownTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly auth: Auth,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly onboardingService: AuthOnboardingService,
    private readonly subscriptionService: SubscriptionService
  ) {
    if (!this.auth.currentUser) {
      void this.router.navigate(['/login']);
    }
  }

  async checkVerification(): Promise<void> {
    const user = this.auth.currentUser;
    if (!user || this.isChecking()) return;
    this.isChecking.set(true);
    this.message.set(null);
    this.errorMessage.set(null);

    try {
      await user.reload();
      if (!user.emailVerified) {
        this.errorMessage.set('Your email is not verified yet. Open the latest verification email and try again.');
        return;
      }

      await user.getIdToken(true);
      await this.onboardingService.ensureUserProvisioned(user, this.readPendingProfile());
      sessionStorage.removeItem('pendingRegistrationProfile');
      this.navigateAfterVerification();
    } catch (error) {
      this.errorMessage.set(this.authErrorMessage(error));
    } finally {
      this.isChecking.set(false);
    }
  }

  async resendVerification(): Promise<void> {
    const user = this.auth.currentUser;
    if (!user || this.isResending() || this.resendCooldown() > 0) return;
    this.isResending.set(true);
    this.message.set(null);
    this.errorMessage.set(null);

    try {
      // Do not supply a continue URL. Firebase otherwise blocks delivery when the
      // current deployment hostname is absent from the project's Authorized domains.
      await sendEmailVerification(user);
      this.message.set('A new verification email has been sent. Check your inbox and spam folder.');
      this.startCooldown(60);
    } catch (error) {
      this.errorMessage.set(this.authErrorMessage(error));
    } finally {
      this.isResending.set(false);
    }
  }

  async useAnotherAccount(): Promise<void> {
    await signOut(this.auth);
    sessionStorage.removeItem('pendingRegistrationProfile');
    await this.router.navigate(['/login'], { queryParams: this.preservedQueryParams() });
  }

  ngOnDestroy(): void {
    if (this.cooldownTimer) clearInterval(this.cooldownTimer);
  }

  private startCooldown(seconds: number): void {
    this.resendCooldown.set(seconds);
    if (this.cooldownTimer) clearInterval(this.cooldownTimer);
    this.cooldownTimer = setInterval(() => {
      const next = this.resendCooldown() - 1;
      this.resendCooldown.set(Math.max(0, next));
      if (next <= 0 && this.cooldownTimer) {
        clearInterval(this.cooldownTimer);
        this.cooldownTimer = null;
      }
    }, 1000);
  }

  private readPendingProfile(): OnboardingProfile {
    try {
      return JSON.parse(sessionStorage.getItem('pendingRegistrationProfile') ?? '{}') as OnboardingProfile;
    } catch {
      return {};
    }
  }

  private navigateAfterVerification(): void {
    const redirectTo = this.route.snapshot.queryParamMap.get('redirectTo');
    if (redirectTo?.startsWith('/')) {
      void this.router.navigateByUrl(redirectTo);
      return;
    }
    if (this.subscriptionService.getSelectedPlan()) {
      void this.router.navigate(['/checkout']);
      return;
    }
    void this.router.navigate(['/dashboard']);
  }

  private preservedQueryParams(): Record<string, string> | null {
    const params: Record<string, string> = {};
    for (const key of ['redirectTo', 'ref']) {
      const value = this.route.snapshot.queryParamMap.get(key);
      if (value) params[key] = value;
    }
    return Object.keys(params).length ? params : null;
  }

  private authErrorMessage(error: unknown): string {
    const authError = error as { code?: string; message?: string };
    if (authError.code === 'auth/too-many-requests') {
      return 'Too many requests were made. Please wait a few minutes before trying again.';
    }
    if (authError.code === 'auth/unauthorized-continue-uri') {
      return 'The verification email could not be sent because this site is not authorized in Firebase. Please contact support.';
    }
    return authError.message ?? 'We could not verify your account. Please try again.';
  }
}
